import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ProgressBar } from '../components/ProgressBar';
import { Badge } from '../components/Badge';

interface Resp {
  candidate: { name: string; email: string };
  progress: {
    benchmarkPassed: boolean;
    dojoMastered: boolean;
    bestBenchmark?: number;
    bestDojo?: number;
    weakest?: string[];
    badges: string[];
  };
  attempts: {
    id: string;
    phase: 'benchmark' | 'dojo';
    scorePct?: number;
    passed?: boolean;
    status: string;
    submittedAt?: string;
  }[];
}

export function CandidateDashboard() {
  const [data, setData] = useState<Resp | null>(null);
  useEffect(() => {
    api.get<Resp>('/api/take/progress').then(setData).catch(() => setData(null));
  }, []);
  if (!data) return <p className="loading">Loading…</p>;
  return (
    <div className="page-dashboard">
      <h1>Hello, {data.candidate.name}</h1>

      <section className="dashboard-grid">
        <div className="card">
          <h3>Benchmark</h3>
          <ProgressBar value={data.progress.bestBenchmark ?? 0} label="Best score" />
          <p>{data.progress.benchmarkPassed ? '✔ Passed' : 'Not yet passed (target: 50%)'}</p>
        </div>
        <div className="card">
          <h3>Dojo</h3>
          <ProgressBar
            value={data.progress.bestDojo ?? 0}
            label="Best score"
            variant="mastery"
          />
          <p>{data.progress.dojoMastered ? '✔ Mastered' : 'Mastery target: 90%'}</p>
        </div>
        <div className="card">
          <h3>Badges</h3>
          {data.progress.badges.length === 0 ? (
            <p className="muted">None yet.</p>
          ) : (
            <div className="badge-row">
              {data.progress.badges.map((b) => (
                <Badge key={b} kind={b} />
              ))}
            </div>
          )}
        </div>
      </section>

      {data.progress.weakest && data.progress.weakest.length > 0 && (
        <section>
          <h2>Focus areas</h2>
          <p>The Dojo will weight these categories more heavily in your next attempt:</p>
          <ul className="weakness-list">
            {data.progress.weakest.map((c) => (
              <li key={c} className={`cat-${c}`}>
                {c}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2>Attempt history</h2>
        <table className="attempts-table">
          <thead>
            <tr>
              <th>Phase</th>
              <th>Status</th>
              <th>Score</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {data.attempts.map((a) => (
              <tr key={a.id}>
                <td>{a.phase}</td>
                <td>{a.status}</td>
                <td>{a.scorePct ?? '—'}%</td>
                <td>{a.submittedAt ? new Date(a.submittedAt).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
